import { create } from 'zustand';

import {
  markSurveyAcknowledged,
  markSurveyVisited,
  readSurveyAcknowledgements,
  readSurveyVisits,
  type SurveyAcknowledgements,
  type SurveyVisits,
} from './surveyVisits';

type SurveyVisitsState = {
  hydratedUserId: string | null;
  visits: SurveyVisits;
  acknowledged: SurveyAcknowledgements;
  hydrate: (userId: string) => Promise<void>;
  markVisited: (userId: string, surveyGroupId: string) => Promise<void>;
  acknowledge: (userId: string, surveyGroupId: string) => Promise<void>;
};

/**
 * Shared visited-survey state behind the Account resume rows and the tab
 * badge, tracking which surveys a user opened and when, and which of those
 * they have since taken the badge up on.
 *
 * Storage is the durable record, but reading it per consumer leaves both
 * surfaces stale until the next launch: they mount before the survey screen
 * writes, and nothing tells them to re-read. Holding the visits here lets a
 * visit update every consumer in the same tick. `hydratedUserId` is tracked
 * so a sign-in swap re-hydrates rather than serving the previous user's
 * visits.
 */
export const useSurveyVisitsStore = create<SurveyVisitsState>((set, get) => ({
  hydratedUserId: null,
  visits: {},
  acknowledged: {},

  hydrate: async (userId) => {
    if (get().hydratedUserId === userId) {
      return;
    }

    set({ hydratedUserId: userId, visits: {}, acknowledged: {} });

    const [visits, acknowledged] = await Promise.all([
      readSurveyVisits(userId),
      readSurveyAcknowledgements(userId),
    ]);

    // The user can change while the read is in flight; dropping a late result
    // keeps one user's visits from landing under another's session.
    if (get().hydratedUserId !== userId) {
      return;
    }

    // Anything already in state was recorded after the read began, so it is
    // newer than what came back and has to survive hydration.
    set((state) => ({
      visits: { ...visits, ...state.visits },
      acknowledged: { ...acknowledged, ...state.acknowledged },
    }));
  },

  markVisited: async (userId, surveyGroupId) => {
    if (!get().visits[surveyGroupId]) {
      set((state) => ({
        visits: { ...state.visits, [surveyGroupId]: new Date().toISOString() },
      }));
    }

    // Storage decides whether this was a first visit rather than the in-memory
    // map, which can still be empty mid-hydration and would drop a legitimate
    // acknowledgement by mistaking a repeat visit for a new one.
    const isFirstVisit = await markSurveyVisited(userId, surveyGroupId);

    if (isFirstVisit) {
      set((state) => {
        const acknowledged = { ...state.acknowledged };

        delete acknowledged[surveyGroupId];

        return { acknowledged };
      });
    }
  },

  acknowledge: async (userId, surveyGroupId) => {
    set((state) => ({
      acknowledged: { ...state.acknowledged, [surveyGroupId]: new Date().toISOString() },
    }));

    await markSurveyAcknowledged(userId, surveyGroupId);
  },
}));
